/* Reine Logik der Vorhaben (Kapseln, BAUPLAN-PAKETE.md, Abschnitt 9). Ohne React, mit Vitest getestet. */
import { euro } from "../../kern/betrag";
import type { VorhabenArt } from "../../kern/kapsel";
import { tagMitWochentag } from "../../kern/kalender";
import type { Unterpunkt } from "../../kern/modul";
import type { StatusOption } from "../../kern/ui/Statuswahl";
import type { WertOption } from "../../kern/ui/Wertwahl";
import { heute, tageBis } from "../../kern/zeit";
import { RESSORT_FLAECHEN } from "./listenlogik";
import type { Aufgabe, Sichtbarkeit } from "./logik";

export type VorhabenRolle = "leitend" | "mitwirkend";
export const ROLLE: Record<VorhabenRolle, string> = { leitend: "Leitung", mitwirkend: "Mitwirkend" };
/** Rollen mit Erklärung für die Wertwahl (BAUPLAN-FEINSCHLIFF.md, Abschnitt 3.3), kurz aus der Tabelle „Wer darf was“ */
export const ROLLE_OPTIONEN: readonly WertOption<VorhabenRolle>[] = [
  { wert: "leitend", text: ROLLE.leitend, erklaerung: "Verwaltet Personen, sieht und pflegt das Budget." },
  { wert: "mitwirkend", text: ROLLE.mitwirkend, erklaerung: "Sieht die Aufgaben der Kapsel, bearbeitet eigene. Kein Budget." },
];

/** Zeile der View aufgaben_vorhaben_uebersicht. Zahlen nur aus den Aufgaben, die die Person sieht. */
export interface VorhabenKarte {
  id: string;
  name: string;
  beschreibung: string;
  beginn: string | null;
  ende: string | null;
  aktiv: boolean;
  ressort_id: string | null;
  aufgaben_gesamt: number;
  aufgaben_offen: number;
  aufgaben_erledigt: number;
  naechste_frist: string | null;
  /** Leitung, früheste zuerst */
  leitung: string[];
  /** Alle Personen des Vorhabens, Leitung zuerst */
  personen: string[];
  /** Eigene Rolle, beim Vorstand ohne eigene Rolle null */
  meine_rolle: VorhabenRolle | null;
  /** Ordner des Vorhabens in Google Drive (Paket D) */
  drive_url: string | null;
  archiviert_am: string | null;
  /** Programm von und bis (Paket T1), „HH:MM:SS“, Ende vor Beginn heißt nach Mitternacht */
  programm_beginn: string | null;
  programm_ende: string | null;
  /** Ort für die Grafik des Timetables, leer heißt Stralsund */
  ort: string;
  /** Art (BAUPLAN-VORHABEN-ARTEN.md). Fehlt die Spalte noch, ist es eine Veranstaltung. */
  art: VorhabenArt;
  /** false, solange die Migration der Arten fehlt (die View liefert keine Spalte art) */
  art_bekannt: boolean;
  /** Nur beim Förderantrag: Veranstaltung oder Projekt, für das das Geld beantragt wird */
  gehoert_zu: string | null;
  antrag_stand: AntragStand | null;
  foerdergeber: string;
  antragsfrist: string | null;
  nachweisfrist: string | null;
}

const tage = (n: number) => (n === 1 ? "noch 1 Tag" : `noch ${n} Tage`);
const mitJahr = (tag: string) => `${tagMitWochentag(tag)}${tag.slice(0, 4)}`;

/** Zeitraum auf der Karte: „bis Sa. 05.12., noch 52 Tage“, „bis heute“, „vorbei“, „ab Mo. 02.11.“, „laufend“ oder „Datum offen“. */
export function zeitraumKurz(v: Pick<VorhabenKarte, "beginn" | "ende">, heuteTag: string = heute()): string {
  if (v.ende) {
    const n = tageBis(v.ende, heuteTag);
    if (n < 0) return "vorbei";
    if (n === 0) return "bis heute";
    return `bis ${tagMitWochentag(v.ende)}, ${tage(n)}`;
  }
  if (v.beginn) return v.beginn > heuteTag ? `ab ${tagMitWochentag(v.beginn)}` : "laufend";
  return "Datum offen";
}

/** Zeitraum im Kopf eines Vorhabens: „Mo. 02.11. bis Sa. 05.12.2026, noch 52 Tage“, „laufend seit …, ohne Enddatum“, „Datum offen“. */
export function zeitraumLang(v: Pick<VorhabenKarte, "beginn" | "ende">, heuteTag: string = heute()): string {
  if (v.ende) {
    const von = v.beginn ? `${tagMitWochentag(v.beginn)} bis ` : "bis ";
    const n = tageBis(v.ende, heuteTag);
    return `${von}${mitJahr(v.ende)}${n > 0 ? `, ${tage(n)}` : n === 0 ? ", endet heute" : ", vorbei"}`;
  }
  if (v.beginn) return v.beginn > heuteTag ? `ab ${mitJahr(v.beginn)}, ohne Enddatum` : `laufend seit ${mitJahr(v.beginn)}, ohne Enddatum`;
  return "Datum offen";
}

/** Fortschritt in Prozent, ohne Aufgaben 0 */
export function vorhabenProzent(v: Pick<VorhabenKarte, "aufgaben_gesamt" | "aufgaben_erledigt">): number {
  return v.aufgaben_gesamt ? Math.round((v.aufgaben_erledigt / v.aufgaben_gesamt) * 100) : 0;
}

/** Eigene Vorhaben (mit Rolle, nicht archiviert), nach Name */
export function meineVorhaben(liste: readonly VorhabenKarte[]): VorhabenKarte[] {
  return liste.filter((v) => v.aktiv && v.meine_rolle).sort((a, b) => a.name.localeCompare(b.name, "de"));
}

/** Höchstens so viele Kapseln unter „Vorhaben“ in der Seitenleiste */
export const NAV_KAPSELN = 5;

/** Unterpunkte unter „Vorhaben“ (BAUPLAN-SEITENLEISTE.md, Abschnitt 7): die eigenen aktiven Vorhaben (leitend oder mitwirkend), auch beim
 *  Vorstand nur die eigenen. Nach Termin (Ende) aufsteigend, ohne Termin hinten, dann nach Name. Punkt in der Farbe des Ressorts, ohne
 *  Ressort grau. Mehr als fünf: die ersten fünf und „Alle Vorhaben“. */
export function kapselUnterpunkte(liste: readonly VorhabenKarte[], farben: ReadonlyMap<string, number> | undefined): Unterpunkt[] {
  const eigene = meineVorhaben(liste)
    .filter((v) => !v.archiviert_am)
    .sort((a, b) => (a.ende ?? "9999-99-99").localeCompare(b.ende ?? "9999-99-99") || a.name.localeCompare(b.name, "de"));
  const punkte: Unterpunkt[] = eigene.slice(0, NAV_KAPSELN).map((v) => {
    const i = v.ressort_id ? farben?.get(v.ressort_id) : undefined;
    return { label: v.name, pfad: `/vorhaben/${v.id}`, farbe: i === undefined ? null : (RESSORT_FLAECHEN[i] ?? null) };
  });
  return eigene.length > NAV_KAPSELN ? [...punkte, { label: "Alle Vorhaben", pfad: "/vorhaben", alle: true }] : punkte;
}

/** Sichtbarkeiten für eine neue Aufgabe (Abschnitt 9.3 und 9.5): mit Vorhaben „Kapsel“ zuerst, „Nur Vorstand“ nur für den Vorstand. */
export function sichtOptionen(mitVorhaben: boolean, vorstand: boolean): Sichtbarkeit[] {
  return [...(mitVorhaben ? (["vorhaben"] as const) : []), ...(vorstand ? (["vorstand"] as const) : []), "mitglieder"];
}

/** Passt die gewählte Sichtbarkeit nicht mehr (Vorhaben gewechselt), gilt die erste erlaubte. */
export function sichtPassend(wahl: Sichtbarkeit, mitVorhaben: boolean, vorstand: boolean): Sichtbarkeit {
  const erlaubt = sichtOptionen(mitVorhaben, vorstand);
  return erlaubt.includes(wahl) ? wahl : (erlaubt[0] ?? "mitglieder");
}

/** „Kapsel“ heißt: … (Hinweis unter dem Schnellanlegen, Abschnitt 9.3) */
export function kapselHinweis(name: string, personen: number, vorstand: boolean): string {
  const wer = personen === 0 ? "noch niemand sonst" : personen === 1 ? "die eine Person" : `die ${personen} Personen`;
  return `„Kapsel“ heißt: sichtbar für den Vorstand und ${wer} von ${name}.${vorstand ? " Aufgaben mit „Nur Vorstand“ sehen Mitglieder der Kapsel nicht." : ""}`;
}

/** Erklärung der Sichtbarkeit im Dialog einer Aufgabe */
export function sichtErklaerung(sicht: Sichtbarkeit, vorhabenName: string | null): string {
  if (sicht === "vorhaben") return `Sichtbar für den Vorstand und die Personen von ${vorhabenName ?? "diesem Vorhaben"}.`;
  if (sicht === "mitglieder") return "Sichtbar für alle Mitglieder.";
  return vorhabenName ? `Sichtbar nur für den Vorstand und wer sie hat. Die Personen von ${vorhabenName} sehen sie nicht.` : "Sichtbar nur für den Vorstand und wer sie hat.";
}

/** Darf die Person Personen des Vorhabens verwalten? Vorstand (vorhaben.verwalten) und Leitung, wie aufgaben_vorhaben_person_setzen. */
export function darfPersonenVerwalten(verwalten: boolean, rolle: VorhabenRolle | null, aktiv: boolean): boolean {
  return aktiv && (verwalten || rolle === "leitend");
}

/** Wen darf die Person als verantwortlich wählen? Vorstand alle Aktiven, Leitung bei Aufgaben ohne „Nur Vorstand“ die Personen des
 *  Vorhabens und sich, sonst nur sich (wie aufgaben_personen_pruefen in der Datenbank). */
export function waehlbareIds(e: { vorstand: boolean; ich: string; aktive: readonly string[]; leitet: boolean; sichtbarkeit: Sichtbarkeit; imVorhaben: readonly string[] }): string[] {
  if (e.vorstand) return [...e.aktive];
  if (e.leitet && e.sichtbarkeit !== "vorstand") return e.aktive.filter((id) => id === e.ich || e.imVorhaben.includes(id));
  return e.aktive.filter((id) => id === e.ich);
}

/** Reihenfolge der Karten: eigene zuerst (mit Rolle, 15.10.), dann je Teil mit Ende zuerst, das nächste vorn, vorbei ganz hinten; ohne Ende
 *  nach Name. Einzelne Vorhaben stehen nicht mehr in der Seitenleiste, deshalb findet man die eigenen hier oben. */
export function sortiereVorhaben<T extends Pick<VorhabenKarte, "name" | "ende" | "meine_rolle">>(liste: readonly T[], heuteTag: string = heute()): T[] {
  const eigen = (v: T) => (v.meine_rolle ? 0 : 1);
  const rang = (v: T) => (!v.ende ? 1 : v.ende < heuteTag ? 2 : 0);
  return [...liste].sort(
    (a, b) => eigen(a) - eigen(b) || rang(a) - rang(b) || (a.ende ?? "").localeCompare(b.ende ?? "") || a.name.localeCompare(b.name, "de"),
  );
}

/** Eingaben für „Neues Vorhaben“ und „Bearbeiten“ prüfen, Grenzen wie in der Datenbank (Name 120, Beschreibung 2000 Zeichen). */
export function vorhabenPruefen(e: { name: string; beschreibung: string; beginn: string | null; ende: string | null }): string | null {
  const name = e.name.trim();
  if (!name) return "Bitte gib einen Namen ein.";
  if (name.length > 120) return "Der Name hat höchstens 120 Zeichen.";
  if (e.beschreibung.trim().length > 2000) return "Die Beschreibung hat höchstens 2000 Zeichen.";
  if (e.beginn && e.ende && e.beginn > e.ende) return "Der Beginn liegt nach dem Ende.";
  return null;
}

/** Link zu Drive (Ordner oder Unterlage): leer oder https:// ohne Leerzeichen, wie der Check in der Datenbank */
export function driveLinkPruefen(url: string, pflicht: boolean): string | null {
  const t = url.trim();
  if (!t) return pflicht ? "Bitte füg den Link ein, er beginnt mit https://." : null;
  if (!/^https:\/\/\S+$/.test(t)) return "Der Link beginnt mit https:// und hat keine Leerzeichen.";
  if (t.length > 2000) return "Der Link ist zu lang.";
  return null;
}

/* ------------------------------------------------------------------------------------------------------------------------------ */
/* Übersicht /vorhaben (BAUPLAN-FLAECHEN.md, Abschnitt 4, Variante A)                                                             */
/* ------------------------------------------------------------------------------------------------------------------------------ */

/** Countdown oben auf der Karte und Spalte „Termin“ in der Liste */
export interface Countdown {
  /** Groß: Zahl der Tage bis zum Ende oder ein Wort („heute“, „vorbei“, „laufend“, „geplant“) */
  gross: string;
  /** Neben der Zahl „Tage“ oder „Tag“, bei einem Wort null */
  einheit: string | null;
  /** Darunter: „bis Sa. 05.12.“, „seit Di. 01.09.“ */
  unter: string;
  /** Kurz für Screenreader und die Liste: „noch 50 Tage“, „endet heute“ */
  kurz: string;
}

export function countdown(v: Pick<VorhabenKarte, "beginn" | "ende">, heuteTag: string = heute()): Countdown {
  if (v.ende) {
    const n = tageBis(v.ende, heuteTag);
    const bis = tagMitWochentag(v.ende);
    if (n < 0) return { gross: "vorbei", einheit: null, unter: `endete ${bis}`, kurz: "vorbei" };
    if (n === 0) return { gross: "heute", einheit: null, unter: `endet heute, ${bis}`, kurz: "endet heute" };
    if (n === 1) return { gross: "1", einheit: "Tag", unter: `bis morgen, ${bis}`, kurz: "noch 1 Tag" };
    return { gross: String(n), einheit: "Tage", unter: `bis ${bis}`, kurz: tage(n) };
  }
  if (v.beginn && v.beginn > heuteTag) {
    const ab = `ab ${tagMitWochentag(v.beginn)}`;
    return { gross: "geplant", einheit: null, unter: ab, kurz: ab };
  }
  if (v.beginn) return { gross: "laufend", einheit: null, unter: `seit ${tagMitWochentag(v.beginn)}`, kurz: "laufend" };
  return { gross: "laufend", einheit: null, unter: "ohne Datum", kurz: "ohne Datum" };
}

/** Abstand zur nächsten Frist: „heute“, „morgen“, „in 7 Tagen“, „seit gestern überfällig“, „seit 3 Tagen überfällig“ */
export function fristAbstand(tag: string, heuteTag: string = heute()): string {
  const n = tageBis(tag, heuteTag);
  if (n < -1) return `seit ${-n} Tagen überfällig`;
  if (n === -1) return "seit gestern überfällig";
  if (n === 0) return "heute";
  if (n === 1) return "morgen";
  return `in ${n} Tagen`;
}

/** Eigene Rolle als Marke unten auf der Karte. Der Vorstand ohne Rolle sieht „Vorstand“. */
export function rolleMarke(rolle: VorhabenRolle | null): { text: string; art: "dunkel" | "ok" | "normal" } {
  if (rolle === "leitend") return { text: "Du leitest", art: "dunkel" };
  if (rolle === "mitwirkend") return { text: "Du machst mit", art: "ok" };
  return { text: "Vorstand", art: "normal" };
}

export type VorhabenFilter = "alle" | "meine";
export type VorhabenAnsicht = "kacheln" | "liste";

/** Aktive Vorhaben im Filter, sortiert wie bisher (sortiereVorhaben). „Meine“: die Person leitet oder macht mit. Archivierte nie. */
export function vorhabenImFilter<T extends Pick<VorhabenKarte, "name" | "ende" | "meine_rolle" | "aktiv">>(
  liste: readonly T[],
  filter: VorhabenFilter,
  heuteTag: string = heute(),
): T[] {
  return sortiereVorhaben(
    liste.filter((v) => v.aktiv && (filter === "alle" || !!v.meine_rolle)),
    heuteTag,
  );
}

/** Zahlen hinter „Alle“ und „Meine“ */
export function filterZahlen(liste: readonly Pick<VorhabenKarte, "aktiv" | "meine_rolle">[]): Record<VorhabenFilter, number> {
  const aktiv = liste.filter((v) => v.aktiv);
  return { alle: aktiv.length, meine: aktiv.filter((v) => !!v.meine_rolle).length };
}

/** „Als Nächstes“ und „Nächste Frist“: die offene Aufgabe mit der frühesten Frist im Vorhaben, aus den Aufgaben, die die Person sieht
 *  (wie naechste_frist der View). Ohne Unteraufgaben und ohne Archiv, bei gleicher Frist nach Titel. */
export function alsNaechstes(liste: readonly Aufgabe[], vorhaben: string): Aufgabe | undefined {
  return liste
    .filter((x) => x.vorhaben_id === vorhaben && !x.eltern_id && x.status !== "erledigt" && !x.archiviert_am && x.frist_datum)
    .sort((x, y) => (x.frist_datum ?? "").localeCompare(y.frist_datum ?? "") || x.titel.localeCompare(y.titel, "de"))[0];
}

/* ------------------------------------------------------------------------------------------------------------------------------ */
/* Arten von Vorhaben (BAUPLAN-VORHABEN-ARTEN.md, ADR-024)                                                                        */
/* ------------------------------------------------------------------------------------------------------------------------------ */

export type ArtZeichen = "ticket" | "euro" | "ziel";

/** Texte je Art, wie im Mockup 04-vorhaben-arten.html (Schritt 1 und 2 des Modals, Marke, Filter) */
export const ART: Record<
  VorhabenArt,
  { name: string; mehrzahl: string; zeichen: ArtZeichen; satz: string; bringt: readonly string[]; beispiel: string; neu: string; anlegen: string; platzhalter: string }
> = {
  veranstaltung: {
    name: "Veranstaltung",
    mehrzahl: "Veranstaltungen",
    zeichen: "ticket",
    satz: "Ein Termin mit Programm, Helfern und Geld.",
    bringt: ["Aufgaben", "Budget", "Line-up", "Unterlagen"],
    beispiel: "Zum Beispiel ein Konzert, ein Fest oder ein Workshop-Abend.",
    neu: "Neue Veranstaltung",
    anlegen: "Veranstaltung anlegen",
    platzhalter: "zum Beispiel Sternenzauber 2026",
  },
  foerderantrag: {
    name: "Förderantrag",
    mehrzahl: "Förderanträge",
    zeichen: "euro",
    satz: "Geld beantragen, von der Idee bis zum Verwendungsnachweis. Mit Stand, Fristen und Summen.",
    bringt: ["Aufgaben", "Stand und Fristen", "Unterlagen"],
    beispiel: "Zum Beispiel bei einer Stiftung, beim Land oder in einem Förderprogramm.",
    neu: "Neuer Förderantrag",
    anlegen: "Antrag anlegen",
    platzhalter: "zum Beispiel Kulturförderung 2027",
  },
  projekt: {
    name: "Projekt",
    mehrzahl: "Projekte",
    zeichen: "ziel",
    satz: "Etwas mit Ziel und Ende, aber ohne Bühne.",
    bringt: ["Aufgaben", "Budget", "Unterlagen"],
    beispiel: "Zum Beispiel eine Mitgliederaktion, ein Umzug oder eine neue Website.",
    neu: "Neues Projekt",
    anlegen: "Projekt anlegen",
    platzhalter: "zum Beispiel Mission 100",
  },
};

export const ART_OPTIONEN: readonly WertOption<VorhabenArt>[] = (["veranstaltung", "foerderantrag", "projekt"] as const).map((a) => ({
  wert: a,
  text: ART[a].name,
  erklaerung: ART[a].satz,
}));

export const alsArt = (x: unknown): VorhabenArt => (x === "foerderantrag" || x === "projekt" ? x : "veranstaltung");

/** Stand eines Förderantrags (Abschnitt 1): Wort, Ton und Zeichen wie im Mockup. Status nie nur als Farbe. */
export type AntragStand = "idee" | "in_vorbereitung" | "eingereicht" | "bewilligt" | "nachweis_eingereicht" | "abgeschlossen" | "abgelehnt";

export const STAENDE: readonly StatusOption<AntragStand>[] = [
  { wert: "idee", text: "Idee", erklaerung: "Noch nicht begonnen.", ton: "grau", zeichen: "kreis" },
  { wert: "in_vorbereitung", text: "In Vorbereitung", erklaerung: "Der Antrag wird geschrieben.", ton: "warten", zeichen: "halbkreis" },
  { wert: "eingereicht", text: "Eingereicht", erklaerung: "Liegt beim Fördergeber.", ton: "weiss", zeichen: "flieger" },
  { wert: "bewilligt", text: "Bewilligt", erklaerung: "Zusage da. Jetzt umsetzen und den Nachweis vorbereiten.", ton: "ok", zeichen: "haken" },
  { wert: "nachweis_eingereicht", text: "Nachweis eingereicht", erklaerung: "Der Verwendungsnachweis liegt beim Fördergeber.", ton: "weiss", zeichen: "blatt" },
  { wert: "abgeschlossen", text: "Abgeschlossen", erklaerung: "Nachweis anerkannt, fertig.", ton: "ok", zeichen: "erledigt" },
  { wert: "abgelehnt", text: "Abgelehnt", erklaerung: "Beendet den Antrag. Danach archivierst du ihn.", ton: "aus", zeichen: "kreuz" },
];

const STAND_REIHE: readonly AntragStand[] = ["idee", "in_vorbereitung", "eingereicht", "bewilligt", "nachweis_eingereicht", "abgeschlossen"];

export const alsStand = (x: unknown): AntragStand | null => (STAENDE.some((s) => s.wert === x) ? (x as AntragStand) : null);
export const standText = (st: AntragStand | null) => STAENDE.find((s) => s.wert === (st ?? "idee"))?.text ?? "Idee";

/** Stände für die Wertwahl (Abschnitt 8.2): „Abgelehnt“ erst ab „Eingereicht“. Ohne Stand gilt „Idee“. */
export function standOptionen(stand: AntragStand | null): readonly StatusOption<AntragStand>[] {
  const frueh = !stand || stand === "idee" || stand === "in_vorbereitung";
  return frueh ? STAENDE.filter((s) => s.wert !== "abgelehnt") : STAENDE;
}

export interface Schritt {
  wert: AntragStand;
  text: string;
  zustand: "erledigt" | "aktuell" | "offen";
}

/** Leiste mit sechs Schritten (Abschnitt 6.2). Bei „Abgelehnt“ sind die Schritte bis „Eingereicht“ erledigt, danach steht statt der übrigen
 *  Schritte die rote Marke „Abgelehnt“ (abgelehnt: true). */
export function standSchritte(stand: AntragStand | null): { schritte: Schritt[]; abgelehnt: boolean } {
  const st = stand ?? "idee";
  if (st === "abgelehnt") {
    const bis = STAND_REIHE.indexOf("eingereicht");
    return { schritte: STAND_REIHE.slice(0, bis + 1).map((w) => ({ wert: w, text: standText(w), zustand: "erledigt" })), abgelehnt: true };
  }
  const i = STAND_REIHE.indexOf(st);
  return {
    schritte: STAND_REIHE.map((w, j) => ({ wert: w, text: standText(w), zustand: j < i ? "erledigt" : j === i ? "aktuell" : "offen" })),
    abgelehnt: false,
  };
}

/** Datum mit Wochentag, mit Jahr nur, wenn es nicht das laufende ist: „Fr. 13.11.“, „Mi. 31.03.2027“ */
export function tagKurz(tag: string, heuteTag: string = heute()): string {
  return tag.slice(0, 4) === heuteTag.slice(0, 4) ? tagMitWochentag(tag) : mitJahr(tag);
}

export interface AntragFrist {
  art: "Antrag" | "Nachweis";
  tag: string;
}

/** Nächste Frist eines Antrags (Abschnitt 8.1): bis zur Einreichung die Antragsfrist, nach der Bewilligung die Nachweisfrist, sonst keine. */
export function naechsteAntragsfrist(v: Pick<VorhabenKarte, "antrag_stand" | "antragsfrist" | "nachweisfrist">): AntragFrist | null {
  const st = v.antrag_stand ?? "idee";
  if ((st === "idee" || st === "in_vorbereitung") && v.antragsfrist) return { art: "Antrag", tag: v.antragsfrist };
  if (st === "bewilligt" && v.nachweisfrist) return { art: "Nachweis", tag: v.nachweisfrist };
  return null;
}

/** Kopf der Kapsel eines Antrags (Abschnitt 6.1): „Antragsfrist Fr. 13.11., noch 27 Tage“, „Nachweis bis Mi. 31.03.2027, noch 165 Tage“.
 *  Ohne Frist null. */
export function antragKopf(v: Pick<VorhabenKarte, "antrag_stand" | "antragsfrist" | "nachweisfrist">, heuteTag: string = heute()): string | null {
  const f = naechsteAntragsfrist(v);
  if (!f) return null;
  const n = tageBis(f.tag, heuteTag);
  const rest = n > 0 ? tage(n) : n === 0 ? "heute" : "vorbei";
  return f.art === "Antrag" ? `Antragsfrist ${tagKurz(f.tag, heuteTag)}, ${rest}` : `Nachweis bis ${tagKurz(f.tag, heuteTag)}, ${rest}`;
}

/** Frist als Zeile mit Art: „Antrag Fr. 13.11. · in 27 Tagen“ (Karte „Förderanträge“) */
export function antragFristZeile(f: AntragFrist, heuteTag: string = heute()): { art: string; tag: string; abstand: string } {
  return { art: f.art, tag: tagKurz(f.tag, heuteTag), abstand: fristAbstand(f.tag, heuteTag) };
}

/** Countdown auf der Kachel eines Antrags (Abschnitt 7.1): Tage bis zur nächsten Frist, darunter „bis zur Antragsfrist Fr. 13.11.“ oder
 *  „bis zum Nachweis Mi. 31.03.2027“. Ohne Frist wie bei den anderen Arten. */
export function countdownAntrag(v: Pick<VorhabenKarte, "beginn" | "ende" | "antrag_stand" | "antragsfrist" | "nachweisfrist">, heuteTag: string = heute()): Countdown {
  const f = naechsteAntragsfrist(v);
  if (!f) return countdown(v, heuteTag);
  const n = tageBis(f.tag, heuteTag);
  const was = f.art === "Antrag" ? "zur Antragsfrist" : "zum Nachweis";
  const tag = tagKurz(f.tag, heuteTag);
  if (n < 0) return { gross: "vorbei", einheit: null, unter: `${f.art === "Antrag" ? "Antragsfrist" : "Nachweis"} war ${tag}`, kurz: "Frist vorbei" };
  if (n === 0) return { gross: "heute", einheit: null, unter: `bis ${was} ${tag}`, kurz: "Frist heute" };
  return { gross: String(n), einheit: n === 1 ? "Tag" : "Tage", unter: `bis ${was} ${tag}`, kurz: n === 1 ? "noch 1 Tag" : tage(n) };
}

/** „Nächste Frist“ auf Kachel und in der Liste (Abschnitt 8.4): beim Antrag die frühere von der Frist der Aufgaben und der des Antrags */
export function naechsteFristTag(
  v: Pick<VorhabenKarte, "art" | "antrag_stand" | "antragsfrist" | "nachweisfrist">,
  aufgabenFrist: string | null | undefined,
): string | null {
  const antrag = v.art === "foerderantrag" ? naechsteAntragsfrist(v)?.tag : undefined;
  const tage = [aufgabenFrist, antrag].filter((x): x is string => !!x).sort();
  return tage[0] ?? null;
}

/** Summen eines Antrags in Cent (die Datenbank speichert Euro, numeric(12,2)) */
export interface Summen {
  beantragt: number | null;
  bewilligt: number | null;
}

/** Summen sieht der Vorstand mit finanzen.verwalten und die Leitung, wie das Budget (Abschnitt 3.2) */
export function darfSummen(finanzen: boolean, rolle: VorhabenRolle | null): boolean {
  return finanzen || rolle === "leitend";
}

export const summeText = (cent: number | null | undefined) => (cent === null || cent === undefined ? "offen" : euro(cent));

/** Kennzahl „Beantragt“ auf der Kachel unter „Vorhaben“: Betrag, darunter der Stand des Antrags */
export function beantragtZusatz(stand: AntragStand | null): string {
  if (!stand || stand === "idee") return "noch nicht eingereicht";
  if (stand === "in_vorbereitung") return "Antrag in Vorbereitung";
  return "eingereicht";
}

export type VorhabenArtFilter = "alle" | VorhabenArt;

export const ART_FILTER_OPTIONEN: readonly WertOption<VorhabenArtFilter>[] = [
  { wert: "alle", text: "Alle" },
  { wert: "veranstaltung", text: ART.veranstaltung.mehrzahl },
  { wert: "foerderantrag", text: ART.foerderantrag.mehrzahl },
  { wert: "projekt", text: ART.projekt.mehrzahl },
];

export const alsArtFilter = (x: unknown): VorhabenArtFilter => (x === "veranstaltung" || x === "foerderantrag" || x === "projekt" ? x : "alle");

/** Filter nach Art (Abschnitt 8.3). Fehlt die Migration noch (keine Art bekannt), zeigt jeder Filter alles. */
export function imArtFilter<T extends Pick<VorhabenKarte, "art" | "art_bekannt">>(liste: readonly T[], filter: VorhabenArtFilter): T[] {
  const bekannt = liste.some((v) => v.art_bekannt);
  return filter === "alle" || !bekannt ? [...liste] : liste.filter((v) => v.art === filter);
}

/** Anträge, die zu einem Vorhaben gehören (Karte „Förderanträge“), aktive zuerst, dann nach nächster Frist und Name */
export function antraegeVon<T extends Pick<VorhabenKarte, "id" | "name" | "art" | "gehoert_zu" | "aktiv" | "antrag_stand" | "antragsfrist" | "nachweisfrist">>(
  liste: readonly T[],
  vorhaben: string,
): T[] {
  return liste
    .filter((v) => v.art === "foerderantrag" && v.gehoert_zu === vorhaben)
    .sort(
      (a, b) =>
        Number(!a.aktiv) - Number(!b.aktiv) ||
        (naechsteAntragsfrist(a)?.tag ?? "9999").localeCompare(naechsteAntragsfrist(b)?.tag ?? "9999") ||
        a.name.localeCompare(b.name, "de"),
    );
}

/** Fuß der Liste der Förderanträge: „2 Anträge · Summe beantragt 2.300,00 € · bewilligt 800,00 €“, nur über die sichtbaren Summen */
export function antraegeFuss(ids: readonly string[], summen: ReadonlyMap<string, Summen>, mitSummen: boolean): string {
  const anzahl = ids.length === 1 ? "1 Antrag" : `${ids.length} Anträge`;
  if (!mitSummen) return anzahl;
  let beantragt = 0;
  let bewilligt = 0;
  for (const id of ids) {
    beantragt += summen.get(id)?.beantragt ?? 0;
    bewilligt += summen.get(id)?.bewilligt ?? 0;
  }
  return `${anzahl} · Summe beantragt ${euro(beantragt)} · bewilligt ${euro(bewilligt)}`;
}
